// src/services/orderSync.ts
// Fetches orders (notifications) and customer messages from the backend API
// and writes them into IndexedDB, replacing any previously synced data.

import { fetchWithAuth } from './auth';
import type { Notification } from '../types/Notification';
import type { QuestionMessage } from '../types/QuestionMessage';
import { API_BASE } from '../config';

// ─── mappers ──────────────────────────────────────────────────────────────────

type OrderedProductRow = {
  id?: number;
  source?: string;
  product_id?: number;
  phone?: string;
  sum?: number;
  status?: string;
  created_at?: string;
  model?: string;
  category_name?: string;
  product_type?: string;
  detail_number?: string;
  count?: number;
  product_price?: number;
  admin_discount?: number;
};

// Backend returns one row per ordered product. Строки одного заказа записываются одним
// запросом и делят телефон и время — по ним и собираем заказ. До 31.10.2026 склейка шла
// по одному телефону: новый заказ постоянного покупателя прилипал к старому, брал его
// время и статус («продан»), и колокольчик его не видел; а смена статуса задевала все
// заказы этого телефона. Номера строк (rowIds) — чтобы менять статус ровно этого заказа.
export function groupOrderRows(rows: OrderedProductRow[]): Notification[] {
  const byOrder = new Map<string, OrderedProductRow[]>();
  for (const row of rows) {
    const key = `${String(row.phone ?? '')}|${String(row.created_at ?? '')}`;
    if (!byOrder.has(key)) byOrder.set(key, []);
    byOrder.get(key)!.push(row);
  }

  const notifications: Notification[] = [];
  for (const group of byOrder.values()) {
    const phone = String(group[0].phone ?? '');
    const firstRow = group[0];
    const createdAt = firstRow.created_at
      ? new Date(firstRow.created_at).toISOString()
      : new Date().toISOString();

    const total = group.reduce((acc, r) => acc + Number(r.sum ?? 0), 0);
    const adminDiscount = Number(group[0]?.admin_discount ?? 0);
    const effectiveTotal = total * (1 - adminDiscount / 100);
    const categories = [...new Set(group.map(r => r.category_name).filter(Boolean))] as string[];
    const productTypes = [...new Set(group.map(r => r.product_type).filter(Boolean))] as string[];
    const skus = group.map(r => r.detail_number).filter(Boolean) as string[];

    const items = group.map((r, idx) => ({
      id: `${phone}_${idx}`,
      name: r.detail_number || r.model || '—',
      type: (r.product_type === 'original' ? 'original' : 'copy') as 'original' | 'copy',
      qty: Number(r.count ?? 1),
      price: Number(r.product_price ?? 0),
      total: Number(r.sum ?? 0),
    }));

    // Use most recent/relevant status from the group
    const rowStatus = firstRow.status ?? 'pending';
    const STATUS_MAP: Record<string, string> = {
      pending:  'new',
      sold:     'shipped',
      canceled: 'canceled',
      return:   'returned',
    };
    const notifStatus = STATUS_MAP[rowStatus] ?? 'new';

    notifications.push({
      id: `${phone}_${createdAt}`,
      number: phone,
      customer: phone,
      phone,
      createdAt,
      total,
      discount: adminDiscount,
      effectiveTotal,
      currency: 'AMD',
      status: notifStatus,
      isRead: false,
      categories,
      productTypes,
      skus,
      items,
      rowIds: group.map(r => Number(r.id)).filter(n => Number.isInteger(n) && n > 0),
    } as any);
  }
  return notifications;
}

type OnorderRow = {
  id?: number | string;
  name?: string;
  phone?: string;
  message?: string;
  status?: string;
  created_at?: string;
};

function mapMessageRow(row: OnorderRow): QuestionMessage {
  return {
    id:        String(row.id ?? ''),
    name:      String(row.name ?? ''),
    phone:     String(row.phone ?? ''),
    message:   String(row.message ?? ''),
    createdAt: row.created_at
      ? new Date(row.created_at).toISOString()
      : new Date().toISOString(),
    isRead:    row.status === 'accepted',
  };
}

// ─── fetch functions (return mapped arrays; state lives in the collection stores) ──

export async function fetchNotifications(): Promise<Notification[]> {
  const res = await fetchWithAuth(`${API_BASE}/order/notifications/pending`);
  if (!res.ok) throw new Error(`Ошибка при загрузке уведомлений: ${res.status}`);

  const data = await res.json() as { orderedProducts?: OrderedProductRow[] };
  return groupOrderRows(data.orderedProducts ?? []);
}

// Статус меняется по номерам строк заказа (rowIds), а не по телефону: по телефону бэкенд
// менял ВСЕ заказы покупателя вместе со складом (до 31.10.2026).
export async function updateOrderStatus(ids: number[], status: 'canceled' | 'return' | 'sold' | 'pending'): Promise<void> {
  if (!ids.length) throw new Error('Нет строк заказа для смены статуса');
  const res = await fetchWithAuth(`${API_BASE}/order/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, status }),
  });
  if (!res.ok) throw new Error(`Ошибка смены статуса: ${res.status}`);
}

export async function markOrdersSold(ids: number[]): Promise<void> {
  if (!ids.length) return;
  // Just update status to sold, don't delete from DB
  await updateOrderStatus(ids, 'sold');
}

// Скидка — на строки одного заказа (rowIds), а не на телефон (до 31.10.2026 — на все заказы покупателя).
export async function setOrderDiscount(ids: number[], discount: number): Promise<void> {
  if (!ids.length) throw new Error('Нет строк заказа для скидки');
  const res = await fetchWithAuth(`${API_BASE}/order/discount`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, discount }),
  });
  if (!res.ok) throw new Error(`Ошибка сохранения скидки: ${res.status}`);
}

export async function deleteMessageById(id: string): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/order/message/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Ошибка удаления сообщения: ${res.status}`);
}

// ─── offline (point-of-sale) sale ───────────────────────────────────────────────

export type OfflineSaleLine = {
  product_id: number;
  product_type: 'original' | 'copy';
  count: number;
};

export type OfflineSaleResult = {
  ok: boolean;
  ref: string;
  total: number;
  count: number;
  payment_method: 'cash' | 'card';
};

export async function createOfflineSale(payload: {
  lines: OfflineSaleLine[];
  phone?: string;
  payment_method: 'cash' | 'card';
}): Promise<OfflineSaleResult> {
  const res = await fetchWithAuth(`${API_BASE}/order/offline-sale`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let msg = `Ошибка оформления продажи: ${res.status}`;
    try {
      const e = await res.json();
      if (e?.message) msg = e.message;
    } catch { /* keep default */ }
    throw new Error(msg);
  }
  return res.json() as Promise<OfflineSaleResult>;
}

export type OfflineSaleRow = {
  id: number;
  product_id: number;
  product_type: 'original' | 'copy';
  count: number;
  product_price: number;
  sum: number;
  payment_method: 'cash' | 'card';
  ref: string;
  created_at: string;
  brand: string;
  model: string;
  detail_number: string;
};

export async function fetchOfflineSales(): Promise<OfflineSaleRow[]> {
  const res = await fetchWithAuth(`${API_BASE}/order/offline-sales`);
  if (!res.ok) throw new Error(`Ошибка загрузки истории продаж: ${res.status}`);
  const rows = await res.json() as any[];
  return rows.map((r) => ({
    id:            Number(r.id),
    product_id:    Number(r.product_id),
    product_type:  r.product_type === 'copy' ? 'copy' : 'original',
    count:         Number(r.count ?? 0),
    product_price: Number(r.product_price ?? 0),
    sum:           Number(r.sum ?? 0),
    payment_method: r.payment_method === 'card' ? 'card' : 'cash',
    ref:           String(r.ref ?? ''),
    created_at:    r.created_at ? new Date(r.created_at).toISOString() : new Date().toISOString(),
    brand:         String(r.brand ?? ''),
    model:         String(r.model ?? ''),
    detail_number: String(r.detail_number ?? ''),
  }));
}

export async function deleteOfflineSaleLine(id: number): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/order/offline-sale/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Ошибка удаления продажи: ${res.status}`);
}

export async function updateOfflineSaleLine(
  id: number,
  data: { count?: number; payment_method?: 'cash' | 'card' },
): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/order/offline-sale/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Ошибка изменения продажи: ${res.status}`);
}

export async function fetchMessages(): Promise<QuestionMessage[]> {
  const res = await fetchWithAuth(`${API_BASE}/order/messages/pending`);
  if (!res.ok) throw new Error(`Ошибка при загрузке сообщений: ${res.status}`);

  const rows = await res.json() as OnorderRow[];
  return rows.map(mapMessageRow);
}
